import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, Trophy, Users, ArrowRight, Armchair } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { meAPI } from '@/lib/api'

/* HomeQuickStrip — 4 kleine info-tegels bovenaan Home:
   - Volgende programma-item (over X min)
   - Jouw volgende toernooi-match (indien deelnemer)
   - Raffle-tickets teller (indien module aan + tickets)
   - Online spelers (users met login in laatste 30 min)

   Elke tegel verbergt zichzelf als er niks te tonen is. Bij lege response:
   het hele blok verdwijnt. */

function relTime(iso) {
  if (!iso) return null
  const s = Math.floor((new Date(iso) - Date.now()) / 1000)
  if (s < 60) return 'zo dadelijk'
  if (s < 3600) return `over ${Math.floor(s / 60)} min`
  if (s < 86400) return `over ${Math.floor(s / 3600)}u`
  return new Date(iso).toLocaleDateString('nl-BE', { weekday: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function HomeQuickStrip() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let alive = true
    const load = () => meAPI.homeWidgets()
      .then(r => alive && setData(r.data))
      .catch(() => alive && setData({}))
      .finally(() => alive && setLoading(false))
    load()
    // Elke 20s pollen (goede balans tussen "snel merkt hij het" tijdens de
    // LAN en niet-DB-spammen). Plus: als de tab weer zichtbaar wordt meteen
    // een fetch — dan hoef je niet tot de volgende tick te wachten.
    const t = setInterval(load, 20_000)
    const onVis = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVis)
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  if (loading || !data) return null
  const tiles = []
  if (data.next_slot) tiles.push({
    key: 'slot',
    href: '/programma',
    icon: CalendarClock,
    eyebrow: 'Volgende programma',
    title: data.next_slot.title,
    subtitle: relTime(data.next_slot.starts_at),
  })
  if (data.next_match) {
    const m = data.next_match
    const opp = m.team_a && m.team_b ? `${m.team_a} vs ${m.team_b}` : (m.game || 'Match')
    tiles.push({
      key: 'match',
      href: `/tournaments/${m.tournament_id}`,
      icon: Trophy,
      eyebrow: 'Jouw volgende match',
      title: opp,
      subtitle: `${m.tournament_name} · R${m.round}·M${m.match_number}` + (m.scheduled_time ? ` · ${relTime(m.scheduled_time)}` : ''),
      hot: true,
    })
  }
  if (data.my_seat) {
    const s = data.my_seat
    // "A1" of "A · #2" — als tafel enkel een letter is (rij-conventie) tonen
    // we tafel+seat gecombineerd; anders enkel het label (want dan is de
    // tafel al haar eigen unieke naam).
    const isRijLetter = /^[A-Za-z]{1,3}$/.test(String(s.table_label || ''))
    const title = isRijLetter && s.seat_number
      ? `${s.table_label}${s.seat_number}`
      : s.table_label || `Stoel ${s.seat_number}`
    tiles.push({
      key: 'seat',
      href: '/seats',
      icon: Armchair,
      eyebrow: s.has_crown ? '👑 Jouw stoel' : 'Jouw stoel',
      title,
      subtitle: s.has_crown ? 'Kroontje van de admin' : 'Op de zaalmap',
      hot: !!s.has_crown,
    })
  }
  if (data.online_count > 0) tiles.push({
    key: 'online',
    href: '/spelers',
    icon: Users,
    eyebrow: 'Online',
    title: `${data.online_count} ${data.online_count === 1 ? 'speler' : 'spelers'}`,
    subtitle: 'Actief in de laatste 10 min',
  })

  if (tiles.length === 0) return null
  // Expliciete klassenlijst — Tailwind JIT scant statisch en compileert geen
  // interpolated class-names, dus `lg:grid-cols-${N}` valt anders terug op
  // sm:grid-cols-2 en de derde+vierde tegel worden nooit breed.
  const lgCols = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' }[Math.min(tiles.length, 4)]
  return (
    <div className={`grid gap-3 grid-cols-1 sm:grid-cols-2 ${lgCols}`}>
      {tiles.map(t => <QuickTile key={t.key} {...t} />)}
    </div>
  )
}

function QuickTile({ href, icon: Icon, eyebrow, title, subtitle, hot }) {
  const cls = `transition-colors hover:border-acid ${hot ? 'border-acid' : 'border-border'}`
  const style = hot ? { borderLeft: '2px solid rgb(var(--acid-rgb))' } : {}
  const inner = (
    <CardContent className="p-4 flex items-start gap-3">
      <div className={`h-10 w-10 rounded-[4px] border flex items-center justify-center shrink-0 bg-card
                       ${hot ? 'border-acid/60' : 'border-border'}`}>
        <Icon className={`h-5 w-5 ${hot ? 'text-acid' : 'text-muted-foreground'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">{eyebrow}</div>
        <div className="font-semibold text-sm truncate mt-0.5">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      {href && <ArrowRight className="h-4 w-4 text-muted-foreground self-center shrink-0" />}
    </CardContent>
  )
  return href
    ? <Link to={href} className="block no-underline"><Card className={cls} style={style}>{inner}</Card></Link>
    : <Card className={cls} style={style}>{inner}</Card>
}
